<script>
import { getPaymentMethod } from "@src/helpers/payment";
import { formatPhone } from "@src/helpers/phone";
import { formatDateBR } from "@src/helpers/date";
import SaleStatusBadge from "@src/components/ui-design/badges/SaleStatusBadge.vue";

export default {
  name: "SalesTable",
  components: {
    SaleStatusBadge,
  },
  props: {
    sales: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      getPaymentMethod,
      formatPhone,
      formatDateBR,
      sortBy: "id",
      sortDesc: true,
      fields: [
        { key: "transaction_id", label: "ID Transação", sortable: true },
        { key: "id", label: "ID Greenn", sortable: true },
        { key: "_client", label: "Cliente", sortable: false },
        { key: "_seller", label: "Vendedor", sortable: false },
        { key: "created_at", label: "Data", sortable: false },
        { key: "_product", label: "Produto", sortable: false },
        { key: "method", label: "Método de Pag.", sortable: false },
        { key: "status", label: "Status", sortable: false },
        { key: "actions", label: "", sortable: false },
      ],
    };
  },
  methods: {
    copyToClipboard(title, value) {
      if (typeof navigator === "undefined" || !navigator.clipboard) {
        this.$bvToast.toast("Recurso de copiar não suportado neste navegador.", {
          title: "Aviso",
          variant: "danger",
          solid: true,
          autoHideDelay: 2000,
        });
        return;
      }

      navigator.clipboard
        .writeText(value)
        .then(() => {
          this.$bvToast.toast(title, {
            title: "Sucesso",
            variant: "success",
            solid: true,
            autoHideDelay: 2000,
          });
        })
        .catch(() => {
          this.$bvToast.toast("Erro ao copiar", {
            title: "Erro",
            variant: "danger",
            solid: true,
          });
        });
    },
  },
};
</script>

<template>
  <div class="table-responsive mb-0">
    <b-table :items="sales" :fields="fields" responsive="sm" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc"
      sort-icon-left class="sales-table">
      <!-- Id -->
      <template v-slot:cell(transaction_id)="data">
        <div class="d-flex align-items-center">
          <p class="sales-table__text--primary mb-0 font-weight-normal" :title="data.value">
            <template v-if="data.value">
              #{{ String(data.value).length >= 10 ? String(data.value).slice(0, 10) + "..." : String(data.value) }}
            </template>
          </p>
          <button class="sales-table__copy" v-if="data.value" type="button"
            @click="copyToClipboard('ID Transação copiado!', String(data.value))"
            aria-label="Copiar ID Transação">
            <img :src="require('@assets/img/icons/copy.svg')" alt="Copiar" title="Copiar" />
          </button>
        </div>
      </template>
      <template v-slot:cell(id)="data">
        <div class="d-flex align-items-center">
          <p class="sales-table__text--primary mb-0 font-weight-normal" :title="data.value"> #{{ data.item.id }} </p>
          <button class="sales-table__copy" type="button"
            @click="copyToClipboard('ID Greenn copiado!', String(data.value))" aria-label="Copiar ID Greenn">
            <img :src="require('@assets/img/icons/copy.svg')" alt="Copiar" title="Copiar" />
          </button>
        </div>
      </template>
      <!-- Client -->
      <template v-slot:cell(_client)="data">
        <span class="sales-table__text--primary">{{
          data.item.client ? data.item.client.name : ""
          }}</span><br />
        <span class="sales-table__text--secondary">{{
          data.item.client ? data.item.client.email : ""
          }}</span>
        <br />
        <span class="sales-table__text--secondary">{{
          data.item.client ? formatPhone(data.item.client.cellphone) : ""
          }}</span>
        <br />
        <span class="sales-table__text--secondary">{{
          data.item.client ? data.item.client.document : ""
          }}</span>
      </template>
      <!-- Seller -->
      <template v-slot:cell(_seller)="data">
        <span class="sales-table__text--primary">{{
          data.item.seller ? data.item.seller.name : ""
          }}</span>
        <br />
        <span class="sales-table__text--secondary">{{
          data.item.seller ? data.item.seller.email : ""
          }}</span>
        <br />
        <span class="sales-table__text--secondary">{{
          data.item.seller ? formatPhone(data.item.seller.cellphone) : ""
          }}</span>
        <br />
        <span class="sales-table__text--secondary">{{
          data.item.seller ? data.item.seller.cpf_cnpj : ""
          }}</span>
      </template>
      <!-- Date -->
      <template v-slot:cell(created_at)="data">
        <span class="sales-table__text--primary font-weight-normal">
          {{ data.value ? formatDateBR(data.value) : "-" }}
        </span>
      </template>
      <!-- Product -->
      <template v-slot:cell(_product)="data">
        <span class="sales-table__text--primary">{{
          data.item.product ? data.item.product.name : ""
          }}</span>
        <br />
        <span v-if="data.item.product" class="sales-table__text--secondary">#{{ data.item.product.id }}</span>
      </template>
      <!-- Method -->
      <template v-slot:cell(method)="data">
        <div>{{ getPaymentMethod(data.value) }}</div>
      </template>
      <!-- Status -->
      <template v-slot:cell(status)="data">
        <SaleStatusBadge :status="data.value" />
      </template>
      <!-- Actions -->
      <template v-slot:cell(actions)="data">
        <router-link :to="{ name: 'vendas-details-next', params: { id: data.item.id } }" title="Ver detalhes"
          :aria-label="`Ver detalhes da venda #${data.item.id}`">
          <div class="sales-table__action">
            <i class="mdi mdi-chevron-right"></i>
          </div>
        </router-link>
      </template>
      <template #empty>
        <div class="text-center py-3">Nenhuma venda encontrada...</div>
      </template>
    </b-table>
  </div>
</template>

<style lang="scss">
.sales-table {
  table {
    border: 0;
  }

  thead {
    background: #f0f2f580;

    th {
      border-top: 0px;
    }
  }

  th {
    border: 0 !important;
    padding-top: 22px;
    padding-bottom: 22px;
    font-weight: 600;
    font-size: 14px;
    color: #000c0b;
  }

  td {
    vertical-align: middle;
    color: #000c0b;
  }

  td:last-child {
    padding: 0 !important;
    width: 67px;
  }

  &__action {
    cursor: pointer;
    height: 118px;
    display: flex;
    align-items: center;
    justify-content: center;

    i {
      font-size: 26px;
      color: #999e9d;
      margin-right: 1rem
    }
  }

  &__copy {
    display: flex;
    border: 0;
    background: none;
    padding: 0;
    margin-left: 8px;

    img {
      width: 16px;
      height: 16px;
      cursor: pointer;
    }
  }

  &__text {
    &--primary {
      color: #000c0b;
      font-weight: 600;
    }

    &--secondary {
      color: #666d6d;
    }
  }
}
</style>
